// src/hooks/useAnalyticsLive.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — live state for the Analytics Hub.
//
// const live = useAnalyticsLive({ enabled, feedLimit })
//   live.status     websocket status: connected | connecting | reconnecting | disconnected | waiting
//   live.feed       newest-first AI activity entries (first paint from /ml-ops/activity, then live)
//   live.unread     entries received since markRead()
//   live.version    increments (debounced 1.5 s) whenever an event that changes analytics arrives —
//                   pass it to dashboards / the briefing so they refetch only when needed
//   live.lastEvent  { type, payload, at } of the newest such event
//   live.counts     events per type since the page opened
//   live.markRead(), live.refreshFeed(), live.clearFeed()
//
// Events received while the tab is hidden are coalesced into one version bump when it is shown
// again, so a backgrounded Analytics tab does not refetch every dashboard repeatedly.
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useRef, useState } from 'react';
import webSocketService from '../services/websocketService';
import { getAiActivity, subscribeToAnalyticsLive } from '../services/analyticsService';

const keyOf = (e) => `${e.id || ''}|${e.kind}|${e.title}|${e.at}`;
// job progress ticks do not change any analytics number
const isNoise = (type, payload) => type === 'ml_events' && (payload?.type === 'job_update' || payload?.event === 'job_update');

export default function useAnalyticsLive({ enabled = true, feedLimit = 50, debounceMs = 1500 } = {}) {
  const [status, setStatus] = useState(() => webSocketService?.status || (webSocketService?.isConnected ? 'connected' : 'idle'));
  const [feed, setFeed] = useState([]);
  const [unread, setUnread] = useState(0);
  const [version, setVersion] = useState(0);
  const [lastEvent, setLastEvent] = useState(null);
  const [counts, setCounts] = useState({});
  const [loadingFeed, setLoadingFeed] = useState(false);
  const [feedError, setFeedError] = useState(null);

  const mounted = useRef(true);
  const bumpTimer = useRef(null);
  const pendingHidden = useRef(false);
  const seen = useRef(new Set());

  const pushFeed = useCallback((entries, { live = false } = {}) => {
    if (!mounted.current || !entries?.length) return;
    // dedupe outside the state updater (updaters must stay pure — StrictMode runs them twice)
    const fresh = entries.filter((e) => {
      const k = keyOf(e);
      if (seen.current.has(k)) return false;
      seen.current.add(k);
      return true;
    });
    if (!fresh.length) return;
    if (seen.current.size > 1000) seen.current = new Set(fresh.map(keyOf));
    setFeed((prev) => [...fresh, ...prev].sort((a, b) => new Date(b.at) - new Date(a.at)).slice(0, feedLimit));
    if (live) setUnread((u) => u + fresh.length);
  }, [feedLimit]);

  const refreshFeed = useCallback(async () => {
    if (!enabled) return;
    setLoadingFeed(true);
    try {
      const rows = await getAiActivity({ limit: feedLimit });
      if (!mounted.current) return;
      setFeedError(null);
      pushFeed(Array.isArray(rows) ? rows : []);
    } catch (e) {
      if (mounted.current) setFeedError(e);
    } finally {
      if (mounted.current) setLoadingFeed(false);
    }
  }, [enabled, feedLimit, pushFeed]);

  const scheduleBump = useCallback(() => {
    if (typeof document !== 'undefined' && document.hidden) { pendingHidden.current = true; return; }
    if (bumpTimer.current) return; // already scheduled — coalesce
    bumpTimer.current = setTimeout(() => {
      bumpTimer.current = null;
      if (mounted.current) setVersion((v) => v + 1);
    }, debounceMs);
  }, [debounceMs]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (bumpTimer.current) clearTimeout(bumpTimer.current);
    };
  }, []);

  useEffect(() => { refreshFeed(); }, [refreshFeed]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onStatus = (evt) => { if (mounted.current && evt?.status) setStatus(evt.status === 'failed' ? 'waiting' : evt.status); };
    const unsubStatus = webSocketService.subscribe('status', onStatus);
    try { if (!webSocketService.isConnected && !webSocketService.isConnecting) webSocketService.connect(); } catch (_) { /* optional */ }

    const unsubLive = subscribeToAnalyticsLive((type, payload) => {
      if (!mounted.current) return;
      if (type === 'ai_activity') {
        if (payload && payload.title) pushFeed([payload], { live: true });
      }
      if (isNoise(type, payload)) return;
      setCounts((c) => ({ ...c, [type]: (c[type] || 0) + 1 }));
      setLastEvent({ type, payload, at: Date.now() });
      scheduleBump();
    });

    const onVis = () => {
      if (!document.hidden && pendingHidden.current) { pendingHidden.current = false; scheduleBump(); }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      if (typeof unsubStatus === 'function') unsubStatus(); else webSocketService.unsubscribe('status', onStatus);
      unsubLive();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [enabled, pushFeed, scheduleBump]);

  const markRead = useCallback(() => setUnread(0), []);
  const clearFeed = useCallback(() => { setFeed([]); setUnread(0); seen.current = new Set(); }, []);

  return { status, feed, unread, markRead, version, lastEvent, counts, refreshFeed, clearFeed, loadingFeed, feedError };
}
